import React from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Facebook, Instagram, Youtube, Linkedin, Mail, Phone, MapPin, ArrowRight } from "lucide-react";

export const Footer = ({ onContactClick }) => {
  const cities = [
    { name: "Bangalore", areas: ["HSR Layout", "HRBR Layout", "JP Nagar", "Whitefield", "Dommasandra"] },
    { name: "Hyderabad", areas: ["Gachibowli"] },
    { name: "Chennai", areas: ["Velachery"] },
  ];

  const quickLinks = [
    { label: "About Us", href: "#" },
    { label: "Our Services", href: "#services" },
    { label: "Portfolio", href: "#portfolio" },
    { label: "Testimonials", href: "#testimonials" },
    { label: "FAQ", href: "#faq" },
    { label: "Contact Us", href: "#" },
  ];

  return (
    <footer className="bg-[hsl(var(--brand-onyx))] text-white pt-16" data-testid="footer">
      <div className="container">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div>
            <h3 className="font-display text-3xl font-semibold text-[hsl(var(--brand-champagne))] mb-4">
              Homz Interio
            </h3>
            <p className="text-white/70 text-sm leading-relaxed mb-6">
              Opulent interiors, crafted to live. 9+ years of excellence in
              transforming homes across India.
            </p>
            <div className="flex gap-3">
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[hsl(var(--brand-gold))] transition-colors"
                aria-label="Facebook"
              >
                <Facebook className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[hsl(var(--brand-gold))] transition-colors"
                aria-label="Instagram"
              >
                <Instagram className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[hsl(var(--brand-gold))] transition-colors"
                aria-label="Youtube"
              >
                <Youtube className="w-5 h-5" />
              </a>
              <a
                href="#"
                className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-[hsl(var(--brand-gold))] transition-colors"
                aria-label="LinkedIn"
              >
                <Linkedin className="w-5 h-5" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="font-serif-alt text-lg font-semibold text-[hsl(var(--brand-champagne))] mb-4">
              Quick Links
            </h4>
            <ul className="space-y-2">
              {quickLinks.map((link) => (
                <li key={link.label}>
                  <a
                    href={link.href}
                    className="text-white/70 text-sm hover:text-[hsl(var(--brand-gold))] transition-colors"
                  >
                    {link.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>

          {/* Experience Centers */}
          <div data-testid="footer-locations">
            <h4 className="font-serif-alt text-lg font-semibold text-[hsl(var(--brand-champagne))] mb-4">
              Experience Centers
            </h4>
            <div className="space-y-4">
              {cities.map((city) => (
                <div key={city.name}>
                  <h5 className="font-semibold text-sm text-[hsl(var(--brand-gold))] mb-1">
                    {city.name}
                  </h5>
                  <p className="text-white/60 text-xs">{city.areas.join(", ")}</p>
                </div>
              ))}
            </div>
          </div>

          {/* Newsletter */}
          <div data-testid="footer-newsletter">
            <h4 className="font-serif-alt text-lg font-semibold text-[hsl(var(--brand-champagne))] mb-4">
              Stay Updated
            </h4>
            <p className="text-white/70 text-sm mb-4">
              Subscribe to get design tips and exclusive offers.
            </p>
            <div className="flex gap-2 mb-4">
              <Input
                type="email"
                placeholder="Your email"
                className="bg-white/10 border-white/20 text-white placeholder:text-white/50"
              />
              <Button variant="gold" size="icon">
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
            <div className="space-y-2 text-sm text-white/70">
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-[hsl(var(--brand-gold))]" />
                <a href="tel:+919876543210" className="hover:text-[hsl(var(--brand-gold))] transition-colors">
                  +91 98765 43210
                </a>
              </div>
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-[hsl(var(--brand-gold))]" />
                <a href="mailto:info@homzinterio.in" className="hover:text-[hsl(var(--brand-gold))] transition-colors">
                  info@homzinterio.in
                </a>
              </div>
            </div>
          </div>
        </div>

        {/* CTA Bar */}
        <div className="py-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div>
              <h3 className="font-serif-alt text-xl font-semibold text-[hsl(var(--brand-champagne))] mb-1">
                Ready to Transform Your Home?
              </h3>
              <p className="text-white/60 text-sm">
                Schedule a free consultation with our design experts today.
              </p>
            </div>
            <Button variant="gold" size="lg" onClick={onContactClick} data-testid="footer-cta-button">
              Get Started
              <ArrowRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className="py-6 border-t border-white/10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-white/60">
          <p>&copy; 2024 Homz Interio. All rights reserved.</p>
          <div className="flex gap-6">
            <a href="#" className="hover:text-[hsl(var(--brand-gold))] transition-colors">
              Privacy Policy
            </a>
            <a href="#" className="hover:text-[hsl(var(--brand-gold))] transition-colors">
              Terms & Conditions
            </a>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
